/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { useEffect, useRef, useState } from "react"
import { SiteService } from "@/services/SiteService"
import { CodeService, Code, FeatureCode, type IPolylineFeatureType, type IPolygonFeatureType, type IAnnulusFeatureType } from "@/services/CodeService"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { AlertCircle, RotateCcw, RotateCw, Pencil, PencilOff, Eye, Loader2 } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { AnnulusPointOfInterest, Feature, GeometeryType, PointPointOfInterest, PolygonPointOfInterest, PolylinePointOfInterest, type Site } from "@/lib/models"
import { Download } from "lucide-react";
import { PolygonBoundaryGenerator } from "@/lib/polygon-boundary-generator"
import { createBlueDotSvg, createTargetSvg } from "@/lib/svg-utils"
import { ActionList } from "@/components/actions/action-list"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { MultiSelect } from "@/components/ui/multi-select"
import { MapSelectedFeatures } from "@/components/maps/googlemaps-selected-features"
import { useNavigate, useParams, useSearchParams } from "react-router"
import { featureTasksUrlBuilder as featureTasksUrlBuilder } from "@/lib/url-builder"
import { FeatureService } from "@/services/FeatureService"
import { formatArea, formatLength } from "@/lib/geospacial-utils"
import { FeatureTypeService, type FeatureType } from "@/services/FeatureTypeService"
import MapObservations from "@/components/observations/MapObservations"
import FeatureGallery from "@/components/feature/FeatureGallery"
import { AttachmentService, AttachmentType, type AttachmentDetails } from "@/services/AttachmenteService"
import { Area, AreaService } from "@/services/AreaService"
import { useForm } from "react-hook-form"
import NewMapObservations from "@/components/observations/NewMapObservations"
import type { GPSCoords } from "@/lib/image-utils"
import FileUploadMain from "@/components/ui/FileUploadMain"
import ImagePreviewDialog from "@/components/ImagePreviewDialog"
import AttachmentImage from "@/components/AttachmentImage"
import { AnnulusContainer, FeatureContainer, PointContainer, PolygonContainer, PolylineContainer } from "@/lib/FeatureTypes"
import { toast } from "react-toastify"
import { useConfig } from "@/config/ConfigProvider"

declare global {
  interface Window {
    google: any
  }
}

const useGoogleMapsScript = (apiKey: string, initializeMap: () => void) => {
  useEffect(() => {
    if (window.google && window.google.maps) {
      initializeMap();
      return;
    }

    const googleMapId = 'google-map'

    const existingScript = document.getElementById(googleMapId)
    if (existingScript) {
      //document.body.removeChild(existingScript)
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=drawing,geometry,marker`;
    //script.async = true;
    script.defer = true;
    script.id = googleMapId
    script.addEventListener('load', initializeMap);
    //document.body.appendChild(script);
    initializeMap();

    return () => {
      script.removeEventListener('load', initializeMap);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);
};

export interface IMapShapeData {
  id: string
  name: string
  codeId: string | null
  polygon: any
  isVisible: boolean
  color: string
  showLabel: boolean
  label?: any
  heading: number
  type: GeometeryType
}

export interface IPolyData {
  id: string
  name: string
  polygon: any
  isVisible: boolean
  color: string
  showLabel: boolean
  label?: any
}

export interface IPolylineData extends IMapShapeData {
  polyline: google.maps.Polyline
}

const calculatePolygonArea = (polygon: any) => {
  if (!polygon || !window.google) return 0
  const path = polygon.getPath()
  return window.google.maps.geometry.spherical.computeArea(path)
}

const calculateAnnalusArea = (polygon: any): number => {
  if (!polygon || !window.google) return 0
  const path = polygon.getPaths()
  const outter = google.maps.geometry.spherical.computeArea(path.getAt(0))
  const inner = google.maps.geometry.spherical.computeArea(path.getAt(1))
  return outter - inner
}

enum MapViewMode {
  Edit,
  View
}

export const MapFeaturePage: React.FC = () => {
  const defaultTabName = "tabDetails"
  const params = useParams();
  const navigate = useNavigate();
  const config = useConfig(); 
  const initalMapViewMode = MapViewMode.View
  const [searchParams] = useSearchParams();

  //const imageMapType = useRef<google.maps.ImageMapType | null>(null);
  const featureList = useRef<FeatureContainer[]>([]);
  const filteredFeatureTypeIds = useRef<string[]>([])
  const featureTypes = useRef<FeatureType[]>([]);
  const areas = useRef<Area[]>([]);
  const canAddAnnulus = useRef<boolean>(false)
  const mapViewModeRef = useRef<MapViewMode>(initalMapViewMode)
  const isCreatingFeatureRef = useRef(false)

  const selectedFeatureContainer = useRef<FeatureContainer | undefined>(undefined)

  const mapRef = useRef<HTMLDivElement>(null)
  const currentPin = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const currentLocationMarker = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const currentMap = useRef<google.maps.Map>(undefined);
  const polyline = useRef<google.maps.Polyline | null>(null);
  const polygon = useRef<google.maps.Polygon | null>(null);
  const selectedType = useRef<GeometeryType | null>(null);
  
  const [map, setMap] = useState<any>(null)
  const [codes, setCodes] = useState<Code[]>([])
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)
  const [showTasks, setShowTasks] = useState<boolean>(false)
  const [isFeatureReportDownloading, setIsFeatureReportDownloading] = useState(false)
  
  // Dialog states
  const [showDeleteFeatureDialog, setShowDeleteFeatureDialog] = useState(false)
  const [deleteFeatureName, setDeleteFeatureName] = useState<string | null>(null)
  const [updateFormIsDirty, setUpdateFormIsDirty] = useState(false)
  const { register, handleSubmit, formState: { errors }, reset } = useForm<Omit<Feature, "id">>({});
  const [ showNewObservationDialog, setShowNewObservationDialog ] = useState<boolean>(false);
  const [ showFeatureDetailsDialog, setShowFeatureDetailsDialog ] = useState<boolean>(true);

  const [showMobileControls, setShowMobileControls] = useState(false)
  const [mapViewMode, setMapViewMode] = useState<MapViewMode>(initalMapViewMode)
  const [showNewGeometerySection, setShowNewGeometerySection] = useState<boolean>(false);
  const [hackSelectedType, setHackSelectedType] = useState<GeometeryType | null>(null);
  const [priorityCodes, setPriorityCodes] = useState<Code[]>([])
  const [conditionCodes, setConditionCodes] = useState<Code[]>([])

  const [selectedGeometryId, setSelectedGeometryId] = useState<string>("");
  const [selectedHabitatCodeId, setSelectedHabitatCodeId] = useState<string>("");
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [selectedCurrentConditionCodeId, setSelectedCurrentConditionCodeId] = useState<string>("");
  const [selectedCurrentConditionSummary, setSelectedCurrentConditionSummary] = useState<string>("")
  const [selectedGeometeryName, setSelectedGeometeryName] = useState<string>("");
  const [selectedMainImage, setSelectedMainImage] = useState<AttachmentDetails | undefined>(undefined);
  const [showMainImageDialog, setShowMainImageDialog] = useState(false);
  const [selectedHabitatTypes, setSelectedHabitatTypes] = useState<Array<GeometeryType>>([]);
  const [selectedPriorityCodeId, setSelectedPriorityCodeId] = useState<string>("");
  const [selectedArea, setSelectedArea] = useState<string | null>(null)

  const [filteredFeatures, setFilteredFeatures] = useState<FeatureContainer[]>([])
  const [featureContainers, setFeatureContainers] = useState<FeatureContainer[]>([])
  const [filterOptions, setFilterOptions] = useState<{label: string, value: string}[]>([])
  const [featureTypeOptions, setFeatureTypeOptions] = useState<FeatureType[]>([])
  const [areaOptions, setAreaOptions] = useState<Area[]>([])
  const [currentTab, setCurrentTab] = useState<string>(defaultTabName);
  const [siteCentre, setSiteCentre] = useState<GPSCoords>({ latitude: 0, longitude: 0 });
  const [site, setSite] = useState<Site | null>(null)

  const initializeMap = () => {
    setIsMapLoaded(true)
    if (!mapRef.current || !window.google) return
  }

  useGoogleMapsScript(config.GOOGLE_MAPS_API_KEY, initializeMap);

  const onSelectedFeatureChange = (value: string) => {
    setSelectedGeometryId(value);
    const selectedItem = featureList.current.find((p) => p.feature.id === value) ?? null;
    if (selectedItem) {
      selectedFeatureContainer.current = selectedItem
      featureList.current.forEach(g => {
        g.setEditable(false, false)
      })
      if (selectedItem.feature.type === GeometeryType.Polygon && selectedItem instanceof PolygonContainer) {
        const area = calculatePolygonArea((selectedItem as PolygonContainer).polygon)
        const perimeter = calculateLineLength((selectedItem as PolygonContainer).polygon.getPath())
        setSelectedArea(`Area ${formatArea(area)} Perimeter ${formatLength(perimeter)}`)
      } else if (selectedItem.feature.type === GeometeryType.Line && selectedItem instanceof PolylineContainer) {
        const length = calculateLineLength((selectedItem as PolylineContainer).polyline.getPath())
        setSelectedArea(`Length ${formatLength(length)}`)
      } else if (selectedItem instanceof AnnulusContainer) {
        const outterArea = calculateAnnalusArea(selectedItem.polygon)
        //const innerArea = calculatePolygonArea(selectedItem.polygon.getPaths().getAt(1))
        const perimeter = calculateLineLength(selectedItem.polygon.getPath())
        setSelectedArea(`Area ${formatArea(outterArea)} Perimeter ${formatLength(perimeter)}`)
      } else {
        setSelectedArea("N/A")
      }
      
      // selectedItem.cordinates.forEach(p => {
      //     bounds.extend({lat: p.latitude, lng: p.longitude})
      // })
      //mapInstance.setZoom(selectedItem.zoom)
      currentMap.current!.setCenter(({lat: selectedItem.feature.centre.latitude, lng: selectedItem.feature.centre.longitude}))
        //mapInstance.setZoom(selectedItem.zoom) 
      //mapInstance.setHeading(selectedItem.heading)
      //bounds.extend(new google.maps.LatLng({lat: 51.29419287950255, lng: -0.6112534816157233}))
      //bounds.extend(new google.maps.LatLng({lat: 51.290702575811885, lng: -0.6168348819385616}))
      //mapInstance.setZoom(selectedItem.zoom)
      setTimeout(() => {
        currentMap.current!.setHeading(selectedItem.feature.heading) 
      }, 500)
      //mapInstance.fitBounds(bounds)
      setUpdateFormIsDirty(false)
      selectedItem.setEditable(mapViewModeRef.current === MapViewMode.Edit, true)
      setSelectedPriorityCodeId(selectedItem.feature.priorityCodeId!)
      setSelectedHabitatCodeId(selectedItem.feature.codeId!);
      setSelectedCurrentConditionCodeId(selectedItem.feature.currentConditionCodeId!)
      setSelectedCurrentConditionSummary(selectedItem.feature.conditionSummary ?? "")
      selectFeatureType(selectedItem.feature.codeId);
      setSelectedGeometeryName(selectedItem.feature.name)
      setSelectedAreaId(selectedItem.feature.areaId ?? null)
      setCurrentTab(defaultTabName)
    }
  }

  const onMapClick = (event: google.maps.MapMouseEvent) => {
    if (!event.latLng) {
      return;
    }

    if (selectedType.current != GeometeryType.Point) {
      const path = selectedType.current === GeometeryType.Polygon ? polygon.current?.getPath() : polyline.current?.getPath()
      if (!path) {
        return
      }
      const newPoint: google.maps.LatLng = new google.maps.LatLng({
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      });

      path.push(newPoint);
      //setPath((prevPath) => [...prevPath, newPoint]);
      
    } else if (selectedType.current === GeometeryType.Point) {
      if (currentPin.current) {
        currentPin.current.map = null
      }
      currentPin.current = new google.maps.marker.AdvancedMarkerElement({
        position: event.latLng,
        map: map,
        title: "point testing",
        gmpDraggable: true
      })
    }
  };

  const getSelectedFeatureContainer = (): FeatureContainer | null => {
    return featureList.current.find((p) => p.feature.id === selectedGeometryId) ?? null;
  }

  const calculateLineLength = (path: google.maps.MVCArray<google.maps.LatLng>): number => {
    if (!polygon || !window.google) return 0
    return google.maps.geometry.spherical.computeLength(path)
  }

  const selectFeatureType = (codeId: string | null) => {
    const code = codes.find((c) => c.id === codeId) as FeatureCode;
    setSelectedHabitatTypes(code?.types.map(t => t.type) ?? []);
  };

  const onAreaMouseOut = (polygon: google.maps.Polygon & { id: string }): void => {
    if (selectedGeometryId != polygon.id) {
      polygon.setOptions({ strokeWeight: 2 });
    }
  }

  function getFeatureType<T>(featureType: FeatureType | undefined, geometryType: GeometeryType, featureId: string | null): T | undefined {
    const type = featureType?.types.find(c => c.type === geometryType) as T;
    if (!type) {
      console.error(`Feature ${featureId} with feature type ${featureType?.id} type not found for geometry type: ${geometryType}`);
    }
    return type;
  }

  const removeVertex = (event: any, featureId: string | null, path: google.maps.MVCArray<google.maps.LatLng>, minimumVertexCount: number) : void => {
    if (
      mapViewModeRef.current !== MapViewMode.Edit ||
      isCreatingFeatureRef.current ||
      selectedFeatureContainer.current?.feature.id !== featureId ||
      event.vertex == null ||
      path.getLength() <= minimumVertexCount
    ) {
      return
    }

    path.removeAt(event.vertex);
    setUpdateFormIsDirty(true)
  }

  const onLineMouseOut = (polygon: google.maps.Polyline & { id: string }): void => {
    if (selectedFeatureContainer.current?.feature.id != polygon.id) {
      polygon.setOptions({ strokeWeight: 2 });
    }
  }

 const initGoogleFeature = (feature: Feature) : FeatureContainer => {
    const coordinates = feature.coordinates.map(c => c.map(p => ({lat: p.latitude, lng: p.longitude})));
    const featureType = featureTypes.current.find(c => c.id === feature.codeId)
    if (!featureType) {
      console.error(`Feature type not found for feature ID: ${feature.id} with codeId: ${feature.codeId}`);
    }
    
    switch(feature.type) {
      case GeometeryType.Line: {
        const polylineFeatureType = getFeatureType<IPolylineFeatureType>(featureType, GeometeryType.Line, feature.id)!
        const polyline = new google.maps.Polyline({
            path: coordinates[0],
            geodesic: true,
            editable: false,
            draggable: false,
            strokeColor:  polylineFeatureType?.style.strokeColour ?? "#00FF00",
            strokeOpacity: 0.7,
            strokeWeight: 3,
            id: feature.id,
            zIndex: 2
        } as google.maps.PolylineOptions & { id: string })

        google.maps.event.addListener(polyline, "click", () => {
          console.log("Clicked polyline ID:", (polyline as google.maps.Polyline & { id: string }).id);
          if (showNewGeometerySection) {
            console.log('edit mode cancel')
            return;
          }
          const id = (polyline as google.maps.Polyline & { id: string }).id
          onSelectedFeatureChange(id)
          //setFeatureListFeatureId(id)
        });

        google.maps.event.addListener(polyline, "rightclick", (event: any) => {
          removeVertex(event, feature.id, polyline.getPath(), 2);
        });

        google.maps.event.addListener(polyline.getPath(), "set_at", () => {
          console.log("Moved polyline point, polyline ID:", (polyline as google.maps.Polyline & { id: string }).id);
          setUpdateFormIsDirty(true)
        })

        polyline.addListener("mouseover", () => {
          polyline.setOptions({ strokeWeight: 5, strokeOpacity: 1.0 });
        });

        polyline.addListener("mouseout", () => {
          onLineMouseOut(polyline as google.maps.Polyline & { id: string })
        });

        return new PolylineContainer(feature, true, true, polyline) 
      }
      case GeometeryType.Polygon: {
        const polygonFeatureType = getFeatureType<IPolygonFeatureType>(featureType, GeometeryType.Polygon, feature.id)!
        const newPolygon = new google.maps.Polygon({
            paths: coordinates,
            geodesic: true,
            editable: false,
            draggable: false,
            fillColor: polygonFeatureType.style.fillColour ?? "#00ff00",
            fillOpacity: polygonFeatureType.style.fillOpacity ?? 0.2,
            strokeColor: polygonFeatureType.style.strokeColour ?? "#0000ff",
            strokeOpacity: polygonFeatureType.style.strokeOpacity ?? 1.0,
            strokeWeight: polygonFeatureType.style.strokeWeight ?? 2,
            id: feature.id,
            zIndex: 1
        } as google.maps.PolygonOptions & { id: string })
        
        google.maps.event.addListener(newPolygon, "click", () => {
          console.log("Clicked polygon ID:", (newPolygon as google.maps.Polygon & { id: string }).id);
          if (canAddAnnulus.current) {
            const boundary = PolygonBoundaryGenerator.createBoundaryPolygon(feature.coordinates[0] ?? [], 4);

            const annulusPolygon = new google.maps.Polygon({
              editable: true,
              draggable: true,
              paths: [ boundary.reverse().map(f => ({lat: f.latitude, lng: f.longitude })), feature.coordinates[0].map(c => ({lat: c.latitude, lng: c.longitude }))] 
            });
            if (polygon.current) {
              polygon.current.setMap(null)
            }
            
            annulusPolygon.setMap(currentMap.current!)
            polygon.current = annulusPolygon
            return
          }

          const id = (newPolygon as google.maps.Polygon & { id: string }).id
          onSelectedFeatureChange(id)
          //setFeatureListFeatureId(id)
        });
        
        google.maps.event.addListener(newPolygon, "rightclick", (event: any) => {
          removeVertex(event, feature.id, newPolygon.getPath(), 3);
        });

        google.maps.event.addListener(newPolygon.getPath(), "set_at", () => {
          console.log("Moved polygon point, polygon ID:", (newPolygon as google.maps.Polygon & { id: string }).id);
          setUpdateFormIsDirty(true)
        })

        google.maps.event.addListener(newPolygon.getPath(), "insert_at", () => {
          console.log("Inserted polygon point, polygon ID:", (newPolygon as google.maps.Polygon & { id: string }).id);
          setUpdateFormIsDirty(true)
        })

        newPolygon.addListener("mouseover", () => {
          newPolygon.setOptions({ strokeWeight: 4 });
        });
        newPolygon.addListener("mouseout", () => {
          onAreaMouseOut(newPolygon as google.maps.Polygon & { id: string })
        });

        return new PolygonContainer(feature , true, true, newPolygon) 
      }
      case GeometeryType.Point: {
        // Create an SVG element for the tree icon (48x48)
        const treeSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        treeSvg.setAttribute('width', '48');
        treeSvg.setAttribute('height', '48');
        treeSvg.setAttribute('viewBox', '0 0 48 48'); // Adjusted viewBox for 48x48

        // Foliage (triangular shape, scaled for 48x48)
        const foliage1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        foliage1.setAttribute('d', 'M24 12L15 21H33L24 12Z'); // Scaled coordinates
        foliage1.setAttribute('fill', '#228B22'); // Dark green
        treeSvg.appendChild(foliage1);

        const foliage2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        foliage2.setAttribute('d', 'M24 18L12 30H36L24 18Z'); // Scaled coordinates
        foliage2.setAttribute('fill', '#32CD32'); // Lighter green
        treeSvg.appendChild(foliage2);

        // Trunk (scaled for 48x48)
        const trunk = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        trunk.setAttribute('x', '21'); // Adjusted for centering
        trunk.setAttribute('y', '30');
        trunk.setAttribute('width', '6'); // Scaled width
        trunk.setAttribute('height', '12'); // Scaled height
        trunk.setAttribute('fill', '#8B4513'); // Brown
        treeSvg.appendChild(trunk); 

        const point = new google.maps.marker.AdvancedMarkerElement({
          position: new google.maps.LatLng({
            lat: coordinates[0][0].lat,
            lng: coordinates[0][0].lng
            }),
          gmpDraggable: false,
          gmpClickable: true,
          content: treeSvg,
          zIndex: 3
        })

        google.maps.event.addListener(point, "dragend", function () {
          console.log(`Ponit ID: ${point.id} moved`)
          setUpdateFormIsDirty(true)
        });
        
        point.id = feature.id ?? ""
        point.addListener("click", () => {
          onSelectedFeatureChange(point.id)
          //setFeatureListFeatureId(point.id)
        })
        return new PointContainer(feature, false, true, point) 
      }
      case GeometeryType.Annulus: {
        const annulusFeatureType = getFeatureType<IAnnulusFeatureType>(featureType, GeometeryType.Annulus, feature.id)!
        const annulus = new google.maps.Polygon({
            paths: coordinates,
            geodesic: true,
            editable: false,
            draggable: false,
            fillColor: annulusFeatureType.style.fillColour ?? "#00ff00",
            fillOpacity: annulusFeatureType.style.fillOpacity ?? 0.2,
            strokeColor: annulusFeatureType.style.strokeColour ?? "#0000ff",
            strokeOpacity: annulusFeatureType.style.strokeOpacity ?? 1.0,
            strokeWeight: annulusFeatureType.style.strokeWeight ?? 2,
            id: feature.id,
            zIndex: 1
        } as google.maps.PolygonOptions & { id: string })
        
        google.maps.event.addListener(annulus, "click", () => {
          console.log("Clicked annulus ID:", (annulus as google.maps.Polygon & { id: string }).id);
          const id = (annulus as google.maps.Polygon & { id: string }).id
          onSelectedFeatureChange(id)
          //setFeatureListFeatureId(id)
        });

        annulus.getPaths().forEach((path) => {
          google.maps.event.addListener(path, "set_at", () => {
            console.log("Moved annulus point, annulus ID:", (annulus as google.maps.Polygon & { id: string }).id);
            setUpdateFormIsDirty(true)
          })

          google.maps.event.addListener(path, "insert_at", () => {
            console.log("Inserted annulus point, annulus ID:", (annulus as google.maps.Polygon & { id: string }).id);
            setUpdateFormIsDirty(true)
          })
        })

        google.maps.event.addListener(annulus, "rightclick", (event: any) => {
          if (event.path == null) {
            return
          }

          removeVertex(event, feature.id, annulus.getPaths().getAt(event.path), 3);
        });

        annulus.addListener("mouseover", () => {
          annulus.setOptions({ strokeWeight: 4 });
        });
        annulus.addListener("mouseout", () => {
          onAreaMouseOut(annulus as google.maps.Polygon & { id: string })
        });

        return new AnnulusContainer(feature , true, true, annulus) 
      }          
      default: 
        throw new Error(`Invalid feature type ${feature.type}`)
    }
  }  

  useEffect(() => {
    return () => {
     featureList.current.forEach(f => f.clearEvents()) 
    }
  }, [])

  useEffect(() => {
    const getAttachments = async () => {
      if (!selectedGeometryId) {
        return
      }
      const attachments = await AttachmentService.getByEntityId(selectedGeometryId)
      setSelectedMainImage(attachments.find(a => a.type === AttachmentType.Main))
    }
    getAttachments()
  }, [selectedGeometryId])

  useEffect(() => {
    const featureId = searchParams.get("featureId")
    if (!featureId || filteredFeatures.length === 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => onSelectedFeatureChange(featureId), 0)
    return () => window.clearTimeout(timeoutId)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, filteredFeatures])

  useEffect(() => {
    const getMap = async () => {
    try {
        
      if (!isMapLoaded){
        return
      }

      const [site, features, codes, featureTypeList, areaList] = await Promise.all([
        SiteService.getSiteById(params.siteId!),
        FeatureService.getBySiteId(params.siteId!),
        CodeService.getCodes2(),
        FeatureTypeService.getBySiteId(params.siteId!),
        AreaService.getBySiteId(params.siteId!)
      ])
      setSite(site);
      featureTypes.current = featureTypeList;
      areas.current = areaList;
      setFeatureTypeOptions(featureTypeList);
      setAreaOptions(areaList);

      setCodes(codes);
      const filterList = [...featureTypeList].sort((a, b) => a.name.localeCompare(b.name)).map(f => ({label: f.name, value: f.id}))
      setFilterOptions(filterList)
      filteredFeatureTypeIds.current = filterList.map(f => f.value)
      //setSelectedFrameworks(filterList.current.map(f => f.value))      
      setSiteCentre({ latitude: site.latitude, longitude: site.longitude })
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: { lat: site.latitude, lng: site.longitude },
        streetViewControl:false,
        fullscreenControl: false,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: google.maps.ControlPosition.LEFT_BOTTOM
        },
        mapTypeId: window.google.maps.MapTypeId.SATELLITE,
        zoom: site.zoom,
        scaleControl: true,
        heading: 0,
        tilt: 0,
        mapId: config.GOOGLE_MAPS_MAP_ID || '7e0b577f05c0e6bde3b3f475',
        maxZoom: 22,
      })

        //map.controls[google.maps.ControlPosition.TOP_CENTER].push()
        const div = document.createElement('div')

        // div.style.backgroundColor = 'white'; // Visible background
        div.style.padding = '10px'; // Add padding for spacing
        // div.style.border = '2px solid #000'; // Optional: border for clarity
        // div.style.zIndex = '10'; // Ensure it’s above map elements

        // Create a button element
        const button = document.createElement('button');
        button.type = 'button';
        button.style.padding = '5px 10px'; // Ensure button is visible
        button.style.height = '35px'
        button.style.fontSize = '15px'
        button.className= "text-center bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border co border-gray-200 p-2 z-10"
        button.addEventListener('click', () => {
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                  const pos = {
                      lat: position.coords.latitude,
                      lng: position.coords.longitude
                  };
                  // Center the map on the user's location
                  mapInstance.setCenter(pos);
                  
                  mapInstance.setZoom(18);
                  if (currentLocationMarker.current) {
                    currentLocationMarker.current.map = null;
                    currentLocationMarker.current = null;
                  }
                  currentLocationMarker.current = new google.maps.marker.AdvancedMarkerElement({
                    position: pos,
                    map: mapInstance,
                    content: createBlueDotSvg(),
                    title: 'Your Location',
                  });
              },
              (error) => {
                // Handle errors (e.g., user denied permission)
                alert('Error getting location: ' + error.message);
              }
            );
          } else {
            // Browser doesn't support geolocation
            alert('Geolocation is not supported by this browser.');
          }
        });

        // Insert SVG before the button text
        button.insertBefore(createTargetSvg(), button.firstChild);
        // Append button to div
        div.appendChild(button);
        
        // Add the control to the map (e.g., top-right corner)
        mapInstance.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(div);

        setPriorityCodes(codes.filter(c => c.type === "Priority"))
        setConditionCodes(codes.filter(c => c.type === "Condition"))
        setMap(mapInstance)
        currentMap.current = mapInstance
        const list: Array<FeatureContainer> = []
        features.forEach(g => {
          const geometery = initGoogleFeature(g);
          list.push(geometery)
        })
        featureList.current = list
        setFeatureContainers(list)
        setFilteredFeatures([])

        mapInstance.addListener("click", onMapClick);
        
        setShowNewGeometerySection(list.length < 1)

        //mapInstance.setZoom(site.zoom)

        setApiError(null)


      } catch (error) {
        console.error("Error initializing map:", error)
        setApiError("Failed to initialize map. Please check your API key configuration.")
      }
    };
    getMap();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapLoaded]);

  useEffect(() => {
    mapViewModeRef.current = mapViewMode
  }, [mapViewMode])

  const rotateMap = (degrees: number) => {
    if (!map) return
    const currentHeading = map.getHeading() || 0
    let newHeading = currentHeading + degrees
    if (newHeading < 0) {
      newHeading += 360
    } else if (newHeading >= 360) {
      newHeading -= 360
    }
    currentMap.current!.setHeading(newHeading)
  }

  const getGeometeryEmoji = (type: GeometeryType) => {
    switch (type) {
      case GeometeryType.Polygon:
        return "🔷";
      case GeometeryType.Line:
        return "📏";
      case GeometeryType.Point:
        return "📍";
      case GeometeryType.Annulus:
        return "⭕";
    }
  };

  const getGeometeryName = (type: GeometeryType) => {
    switch (type) {
      case GeometeryType.Polygon:
        return "Area";
      case GeometeryType.Line:
        return "Line";
      case GeometeryType.Point:
        return "Point";
      case GeometeryType.Annulus:
        return "Annulus";
    }
  };

  const onNewFeatureClick = () => {
    featureList.current.forEach(f => {
      f.setEditable(false, false)
    })
    isCreatingFeatureRef.current = true
    setShowNewGeometerySection(true);
    setSelectedHabitatCodeId("");
    resetNewValues()
  };

  const resetNewValues = () => {
    reset({})
  }

  const emptyStringToNull = (value: string | undefined | null): string | null => {
    if (!value) {
      return null
    }

    if (value === "") {
      return null
    }

    return value
  }

  const getUpdateGeometry = () : Feature | null => {
    const selectedItem = getSelectedFeatureContainer();
    if (!selectedItem) {
      throw new Error(`No geometry fonound with id ${selectedGeometryId}`)
    }
    const tenantId = site?.tenantId ?? params.tenantId;
    const siteId = site?.id ?? params.siteId;
    if (!tenantId || !siteId) {
      toast.error("Tenant ID or Site ID is missing. Cannot update feature.");
      return null;
    }
    switch (selectedItem.feature.type) {
      case GeometeryType.Line:
        return ({
          animalsObserved: null,
          attachments: [],
          centre: selectedItem.feature.centre,
          codeId: selectedHabitatCodeId,
          areaId: selectedAreaId,
          siteId: siteId,
          tenantId: tenantId,
          ref: null,
          conditionSummary: emptyStringToNull(selectedCurrentConditionSummary),
          coordinates: selectedItem.getCoordinates(),
          currentConditionCodeId: selectedCurrentConditionCodeId,
          enhancementOpportunities: null,
          heading: 0,
          hpi: false,
          id: selectedItem.feature.id,
          invasivePlantSpecies: null,
          name: selectedGeometeryName,
          nativePlantSpecies: null,
          plannedConditionCodeId: null,
          plannedDate: null,
          priorityCodeId: selectedPriorityCodeId,
          type: GeometeryType.Line,
          zoom: Math.round(map.getZoom() ?? 15),
          description: null,
        })
      case GeometeryType.Polygon:
        return ({
          animalsObserved: null,
          attachments: [],
          centre: selectedItem.feature.centre,
          codeId: selectedHabitatCodeId,
          areaId: selectedAreaId,
          siteId: siteId,
          tenantId: tenantId,
          ref: null,
          conditionSummary: emptyStringToNull(selectedCurrentConditionSummary),
          coordinates: selectedItem.getCoordinates(),
          currentConditionCodeId: selectedCurrentConditionCodeId,
          enhancementOpportunities: null,
          heading: 0,
          hpi: false,
          id: selectedItem.feature.id,
          invasivePlantSpecies: null,
          name: selectedGeometeryName,
          nativePlantSpecies: null,
          plannedConditionCodeId: null,
          plannedDate: null,
          priorityCodeId: selectedPriorityCodeId,
          type: GeometeryType.Polygon,
          zoom: Math.round(map.getZoom() ?? 15),
          description: null,
        })
      case GeometeryType.Point:
        return new PointPointOfInterest(
          selectedItem.feature.id,
          selectedGeometeryName,
          null,
          tenantId,
          siteId,
          null,
          selectedAreaId,
          selectedPriorityCodeId,
          selectedHabitatCodeId,
          false,
          selectedCurrentConditionCodeId,
          emptyStringToNull(selectedCurrentConditionSummary),
          emptyStringToNull(selectedCurrentConditionSummary),
          null,
          null,
          null,
          null,
          null,
          selectedItem.getCoordinates(),
          0,
          Math.round(map.getZoom() ?? 15), // TODO: Change to text box so user can manually set
          selectedItem.getCoordinates()[0][0],
          []);
      case GeometeryType.Annulus:
        return ({
          animalsObserved: null,
          attachments: [],
          centre: selectedItem.feature.centre,
          codeId: selectedHabitatCodeId,
          areaId: selectedAreaId,
          siteId: siteId,
          tenantId: tenantId,
          ref: null,
          conditionSummary: emptyStringToNull(selectedCurrentConditionSummary),
          coordinates: selectedItem.getCoordinates(),
          currentConditionCodeId: selectedCurrentConditionCodeId,
          enhancementOpportunities: null,
          heading: 0,
          hpi: false,
          id: selectedItem.feature.id,
          invasivePlantSpecies: null,
          name: selectedGeometeryName,
          nativePlantSpecies: null,
          plannedConditionCodeId: null,
          plannedDate: null,
          priorityCodeId: selectedPriorityCodeId,
          type: GeometeryType.Annulus,
          zoom: Math.round(map.getZoom() ?? 15),
          description: null,
        })         
      default:
        throw Error('Invalid geometery type')
    }
  }

  const getNewGeometry = (newFeature: Omit<Feature, "id">) : FeatureContainer => {
    const center = map.getCenter();
    const defaultCondition = conditionCodes.find(x => x.isDefault) ?? conditionCodes[0];
    const defaultPriority = priorityCodes.find(x => x.isDefault) ?? priorityCodes[0];
    const tenantId = site?.tenantId ?? "";
    const siteId = site?.id ?? "";
    switch (hackSelectedType) {
      case GeometeryType.Line: {
          const polylineFeature = new PolylinePointOfInterest(
          null,
          newFeature.name,
          null,
          tenantId,
          siteId,
          null,
          newFeature.areaId ?? null,
          defaultPriority.id,
          newFeature.codeId,
          false,
          defaultCondition.id,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          [polyline.current!.getPath().getArray().map(p => ({ latitude: p.lat(), longitude: p.lng() }))],
          0,
          Math.round(map.getZoom() ?? 15),          
          ({latitude: center!.lat(), longitude: center!.lng()}),
          [])
          return new PolylineContainer(polylineFeature, true, true, polyline.current!) 
        }
      case GeometeryType.Polygon: {
            const polygonFeature = new PolygonPointOfInterest(
            null,
            newFeature.name,
            null,
            tenantId,
            siteId,
            null,
            newFeature.areaId ?? null,
            defaultPriority.id,
            newFeature.codeId,
            false,
            defaultCondition.id,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            [polygon.current!.getPath().getArray().map(p => ({ latitude: p.lat(), longitude: p.lng() }))],
            0,
            Math.round(map.getZoom() ?? 15),          
            ({latitude: center!.lat(), longitude: center!.lng()}),
            [])
          return new PolygonContainer(polygonFeature, true, true, polygon.current!) 
        }
      case GeometeryType.Point: {
          const pointFeature = new PointPointOfInterest(
          null,
          newFeature.name,
          null,
          tenantId,
          siteId,
          null,
          newFeature.areaId ?? null,
          defaultPriority.id,
          newFeature.codeId,
          false,
          defaultCondition.id,
          null,
          null,
          null,
          null,
          null,
          null,
          null,
          [[({ latitude: (currentPin.current!.position!.lat as number), longitude: (currentPin.current!.position!.lng as number) })]],
          0,
          Math.round(map.getZoom() ?? 15),          
          ({latitude: center!.lat(), longitude: center!.lng()}),
          [])
        return new PointContainer(pointFeature, true, true, currentPin.current!) 
      }
      case GeometeryType.Annulus: {
            const annulusFeature = new AnnulusPointOfInterest(
            null,
            newFeature.name,
            null,
            tenantId,
            siteId,
            null,
            newFeature.areaId ?? null,
            defaultPriority.id,
            newFeature.codeId,
            false,
            defaultCondition.id,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            polygon.current!.getPaths().getArray().map(c => c.getArray().map(p => ({ latitude: p.lat(), longitude: p.lng() }))),
            0,
            Math.round(map.getZoom() ?? 15),          
            ({latitude: center!.lat(), longitude: center!.lng()}),
            [])
          return new AnnulusContainer(annulusFeature, true, true, polygon.current!) 
        }
      default:
        throw Error('Invalid geometery type')
    }
  }

  const resetNewDialog = () => {
    isCreatingFeatureRef.current = false
    polygon.current?.setMap(null)
    polyline.current?.setMap(null)
    if (currentPin.current) {
      currentPin.current.map = null
    }

    polygon.current = null
    polyline.current = null
    currentPin.current = null
    selectedType.current = null
    canAddAnnulus.current = false
    setHackSelectedType(null)

    const selectedContainer = getSelectedFeatureContainer()
    selectedContainer?.setEditable(mapViewModeRef.current === MapViewMode.Edit, true)
    setShowNewGeometerySection(false);
    resetNewValues()
  }  

  const onUpdateFeatureClick = () => {
    const selectedFeature = featureList.current.find(g => g.feature.id === selectedGeometryId)
    if (!selectedFeature) {
      console.warn(`Could not find feature ${selectedGeometryId}'`)
      return
    }
    const feature = getUpdateGeometry();
    if (!feature) {
      return;
    }
    selectedFeature.feature = feature;
    FeatureService
      .updateFeature(selectedGeometryId, feature)
      .then(() => {
        setUpdateFormIsDirty(false)
      })
      .catch(() => { console.error('Error updating geometery')})
  }

  const onShowDeleteFeatureDialogClick = async () => {
    const selectedItem = getSelectedFeatureContainer();
    setDeleteFeatureName(selectedItem?.feature.name ?? null)
    setShowDeleteFeatureDialog(true)
  }

  const onDeleteFeatureClick = async () => {
    try{ 
      await FeatureService.delete(selectedGeometryId)
      const deletedFeature = featureList.current.find(g => g.feature.id === selectedGeometryId)
      deletedFeature?.removeMap();
      featureList.current = featureList.current.filter(g => g.feature.id != selectedGeometryId)
      setFeatureContainers(featureList.current)
      setFilteredFeatures((features) => features.filter(g => g.feature.id != selectedGeometryId))
      setSelectedGeometryId("")
      setDeleteFeatureName(null)
      setShowDeleteFeatureDialog(false)
    } catch(error) {
      console.error('Error deleting geometery')
      throw error
    }
  }

  const onCancelNewGeometery = () => {
    resetNewDialog()
  };

  const handleNewFeatureFormSubmit = async (newFeature: Omit<Feature, "id">) => {
    const newGeometry = getNewGeometry(newFeature);

    FeatureService
      .createFeature({...newGeometry.feature, siteId: params.siteId!})
      .then((x) => {
        newGeometry.feature.id = x.id
        const initFeature = initGoogleFeature(newGeometry.feature)
        initFeature.setMap(map)
        isCreatingFeatureRef.current = false
        setShowNewGeometerySection(false)
        featureList.current = [...featureList.current, initFeature]
        setFeatureContainers(featureList.current)
        onSelectedFeatureChange(initFeature.feature.id ?? "")
        polygon.current?.setMap(null)
        polyline.current?.setMap(null)
        polygon.current = null
        canAddAnnulus.current = false;
      })
      .catch(() => { console.error('Error saving geometery')})
  };

  const handleNewFeatureSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    void handleSubmit(handleNewFeatureFormSubmit)(event)
  };

  const onSelectType = (value: GeometeryType) => {
    selectedType.current = value;
    setHackSelectedType(value);
    switch (value) {
      case GeometeryType.Polygon: {
            const newPolygon = new google.maps.Polygon({
            strokeColor: "#3b82f6",
            fillColor: "#00ff00",
            fillOpacity: 0.5,
            strokeWeight: 3,
            strokeOpacity: 0.8,
            clickable: true,
            editable: true,
            zIndex: 1,
          } as google.maps.PolygonOptions & { id: string });

          // google.maps.event.addListener(newPolygon, "click", (event: any) => {
          //   console.log("Clicked polygon ID:", (event as google.maps.Polygon & { id: string }).id);
          // });

          polygon.current = newPolygon;
          polygon.current.setMap(map);
        break; 
      }
      case GeometeryType.Line: {
          const newPolyline = new google.maps.Polyline({
          strokeColor: "#3b82f6",
          strokeWeight: 3,
          strokeOpacity: 0.8,
          clickable: true,
          editable: true,
          zIndex: 1,
        });
        polyline.current = newPolyline;
        polyline.current.setMap(map);
        break; 
      }
      case GeometeryType.Annulus:
        canAddAnnulus.current = true;
        // geometryList.current.forEach(f => {
        //     if (f instanceof PolygonContainer) {
        //       f.setEditable(true)
        //     } else {
        //       f.setEditable(false)
        //     }
        //   } 
        // )
        break;
      case GeometeryType.Point:
        //drawingManager?.setDrawingMode(google.maps.drawing.OverlayType.MARKER)
        // const newPoint = new google.maps.marker.AdvancedMarkerElement({
        //     position: new google.maps.LatLng({
   
        //     map: mapInstance,
        //   })
        // break;
    }
  };

  // ... (include all the other map functionality methods from the original component)

  return (
     <div className="flex flex-1 overflow-hidden bg-gray-50 flex-col" >
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 shrink-0">
        <div className="flex flex-row justify-center">
            <div className="flex flex-row justify-center">
                <MultiSelect
                  options={filterOptions}
                  onValueChange={(selectedCodes) => {
                    const selectedFeatures: FeatureContainer[] = [];
                    featureList.current.forEach(g => {
                      if (selectedCodes.includes(g.feature.codeId!)) {
                        g.setMap(currentMap.current!)
                        selectedFeatures.push(g)
                      } else {
                        g.removeMap()
                      }
                    })
                    setFilteredFeatures(selectedFeatures)
                    filteredFeatureTypeIds.current = selectedCodes
                  }}                
                  defaultValue={[]}
                  placeholder="Select Feature Type(s)"
                  variant="inverted"
                  animation={0}
                  maxCount={2}
                />
            <Button 
              disabled={filterOptions.length < 1 || isFeatureReportDownloading}
              className="bg-gray-500 ml-1"
              aria-description="Generate feature report"
              onClick={async () => {
                setIsFeatureReportDownloading(true)
                try {
                  await SiteService.createFeatureReport(params.siteId!, ({featureTypeIds: filteredFeatureTypeIds.current}))
                } finally {
                  setIsFeatureReportDownloading(false)
                }
              }}>
                {isFeatureReportDownloading ? (
                  <Loader2 size={24} className="animate-spin" />
                ) : (
                  <Download size={24} />
                )}
              </Button>
            </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* <div className="flex">
          <FeatureList 
            currentFeatureId={featureListFeatureId}
            features={featureList.current}
            featureFilter={filteredFeatureTypeIds.current}
            onFeatureSelect={f => {
              onSelectedFeatureChange(f.id ?? "")
            }} 
            onFeatureDelete={f => {
              console.log(featureList.current.find(c => c.feature.id === f.id)); 
              console.log(featureList.current)
              
              featureList.current = [ ...featureList.current.filter(g => g.feature.id != f.id) ]
              console.log(featureList.current)
            }}
            onFeatureSelect={f => {
              onSelectedFeatureChange(f.id ?? "")
            }} 
            onFeatureDelete={f => {
              console.log(featureList.current.find(c => c.feature.id === f.id)); 
              console.log(featureList.current)
              
              featureList.current = [ ...featureList.current.filter(g => g.feature.id != f.id) ]
              console.log(featureList.current)
            }}
            onActionSelect={a => {
              setSelectedActionId(a.id)
              setShowTasks(true)
            }}
            featureCodes={featureCodes.current} // Needs to use FeatureType
            />
        </div> */}
        {/* <div className="flex-1 relative">LAYERS</div> */}
        {/* Map area - takes full width on mobile, 3/4 on desktop */}
        <div className="flex-1 relative">
          <Card className="h-full rounded-none border-0">
            <CardContent className="p-0 h-full relative">
              {apiError ? (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                  <div className="text-center p-8">
                    <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Map Not Available</h3>
                    <p className="text-gray-600">Please configure your Google Maps API key to view the map.</p>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: !showTasks ? 'block' : 'none' }} ref={mapRef} className="w-full h-full" />

                  {/* Map Rotation Controls Overlay */}
                  <div style={{ display: !showTasks ? 'block' : 'none' }} className="absolute top-2 left-1/2 transform -translate-x-1/2 text-center bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200 p-2 z-10">
                    <div className="space-y-3">
                      <div className="flex gap-1">
                        {/* <Button
                          size="sm"l
                          variant="outline"
                          onClick={() => setMapDetails()}
                          disabled={!isMapLoaded}
                          title="Set map"
                          className="h-8 w-8 p-0"
                        >
                          <MapPinHouse className="w-3 h-3" />
                        </Button>                         */}
                        {/* <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowCustomTiles(prev => !prev)
                            console.log(showCustomTiles)
                            if (showCustomTiles){
                              map.overlayMapTypes.push(imageMapType.current);
                            } else {
                              map.overlayMapTypes.clear();
                            }
                          }}
                          disabled={!isMapLoaded}
                          title="Rotate 15° counterclockwise"
                          className="h-8 w-8 p-0"
                        >
                          Toggle
                        </Button> */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => rotateMap(15)}
                          disabled={!isMapLoaded}
                          title="Rotate 15° counterclockwise"
                          className="h-8 w-8 p-0"
                        >
                          <RotateCcw className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => rotateMap(-15)}
                          disabled={!isMapLoaded}
                          title="Rotate 15° clockwise"
                          className="h-8 w-8 p-0"
                        >
                          <RotateCw className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const invertedViewMode = mapViewMode === MapViewMode.Edit ? MapViewMode.View : MapViewMode.Edit
                            setMapViewMode(invertedViewMode)
                            const container = getSelectedFeatureContainer()
                            if (container) {
                              container.setEditable(invertedViewMode === MapViewMode.Edit, true)
                            }
                          }}
                          title="View/Edit feature"
                          className="h-8 w-8 p-0 bg-transparent"
                        >
                          {mapViewMode === MapViewMode.Edit ? <Pencil className="w-3 h-3" /> : <PencilOff className="w-3 h-3" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowNewObservationDialog(true);
                            setShowFeatureDetailsDialog(false)
                            resetNewDialog()
                            setShowMobileControls(true);

                          }}
                          disabled={!isMapLoaded}
                          title="Create new observation"
                          className="h-8 w-8 p-0"
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                        <div className="hidden sm:flex items-center justify-center">
                          <MapSelectedFeatures selectedFeatures={filteredFeatures} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: showTasks ? 'block' : 'none' }} className="w-full h-full">
                    <ActionList featureId={selectedGeometryId} codes={codes} featureName={selectedGeometeryName} />
                  </div>
                  {/* Mobile controls toggle button */}
                  <Button
                    className="lg:hidden absolute top-1/2 right-2 -translate-y-1/2 z-10 text-gray-900 bg-white/95"
                    onClick={() => {
                      setShowMobileControls(!showMobileControls)
                      setShowFeatureDetailsDialog(true)
                      setShowNewObservationDialog(false)
                    }}
                    size="sm"
                  >
                    Details
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - hidden on mobile by default, overlay on mobile when shown */}
        <div
          className={`
        ${showMobileControls ? "fixed inset-y-0 right-0 z-50" : "hidden"} 
        lg:relative lg:block lg:w-80 xl:w-96
        bg-white border-l border-gray-200 overflow-y-auto
        ${showMobileControls ? "w-full shadow-xl" : ""}
      `}
        >
          {/* Mobile close button */}
          {showMobileControls && (
            <div className="lg:hidden p-4 border-b border-gray-200">
              <Button variant="outline" size="sm" onClick={() => setShowMobileControls(false)} className="w-full">
                Close Details
              </Button>
            </div>)}

          <Card className="m-1">
            {showNewObservationDialog && (<NewMapObservations 
              centreCoordinates={siteCentre}
              onClose={() => {
                setShowNewObservationDialog(false)
                setShowMobileControls(false)
                setShowFeatureDetailsDialog(true)
              }}  />)}
              {showNewGeometerySection && 
              <CardHeader>
                <CardTitle className="text-lg">New Feature</CardTitle>
              </CardHeader>}

              <CardContent className="space-y-3">
              {showFeatureDetailsDialog && !showNewGeometerySection && (
                <>
                  <div>        
                    <div className="pt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={mapViewMode === MapViewMode.View}
                          onClick={() => onNewFeatureClick()}
                          className="w-full"
                        >
                          New
                        </Button>
                    </div>  
                    <div className="pt-1">
                      <Label htmlFor="item-select"></Label>
                      <Select
                        value={selectedGeometryId}
                        onValueChange={(value) => {
                          onSelectedFeatureChange(value)
                        }}
                        name="item-select"
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a feature..." />
                        </SelectTrigger>
                        <SelectContent>
                          {featureContainers.length > 0 && (
                            <>
                              {[...featureContainers].sort((a, b) => a.feature.name.localeCompare(b.feature.name)).map((container) => (
                                <SelectItem key={container.feature.id} value={container.feature.id!}>
                                  {/* 🔷 {polygon.name} {!polygon.isVisible && "(Hidden)"} */}
                                  {getGeometeryEmoji(container.feature.type)} {container.feature.name}
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>                    
                    </div>
                  </div>
                  {selectedGeometryId && (<>
                  <Tabs value={currentTab} onValueChange={tab => setCurrentTab(tab)}>
                    <TabsList className="TabsList w-full" aria-label="Manage feature">
                      <TabsTrigger className="TabsTrigger w-full" value={defaultTabName}>
                        Details
                      </TabsTrigger>
                      <TabsTrigger className="TabsTrigger w-full" value="tabGallery">
                        Gallery
                      </TabsTrigger>
                      <TabsTrigger className="TabsTrigger w-full" value="tabObservations">
                        Observations
                      </TabsTrigger>                      
                    </TabsList>
                    <TabsContent className="TabsContent" value={defaultTabName}>
                      <Button
                        className="hidden"
                        onClick={() => { navigate(featureTasksUrlBuilder(params.tenantId!, params.siteId!, selectedGeometryId))}}>
                        Tasks
                      </Button>
                      <Button
                        className="hidden"
                        onClick={() => { setShowTasks(!showTasks)}}>
                        {!showTasks && <>Tasks</>} {showTasks && <>Map</>}
                      </Button>
                      <div className="pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onUpdateFeatureClick()}
                          className="w-full bg-sky-400"
                          disabled={!updateFormIsDirty}
                        >
                          Update
                        </Button>
                      </div>
                      <div>
                        <Label htmlFor="name">Name*</Label>
                        <Input
                          id="name"
                          value={selectedGeometeryName}
                          onChange={(e) => {
                            setSelectedGeometeryName(e.target.value)
                            setUpdateFormIsDirty(true)}}
                          placeholder="Enter feature name..."
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              //savePolygon()
                            }
                          }}
                        />
                      </div>                      
                      <div className="p-1">
                        <span>{selectedArea}</span>
                      </div>
                      <div>
                        <Label htmlFor="habitat-type-select">Feature Type</Label>
                        <Select
                          name="habitat-type-select"
                          value={selectedHabitatCodeId}
                          onValueChange={(value) => {
                            setSelectedHabitatCodeId(value);
                            selectFeatureType(value);
                            setHackSelectedType(null);
                            setUpdateFormIsDirty(true)
                            // setSelectedCode(codes.find(c => c.id === value) ?? null)
                            // selectedPolyline.codeId = value
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a type..." />
                          </SelectTrigger>
                          <SelectContent>
                            {featureTypeOptions.length > 0 && (
                              <>
                                {[...featureTypeOptions].sort((a, b) => a.name.localeCompare(b.name)).map((polygon) => (
                                  <SelectItem key={polygon.id} value={polygon.id ?? ""}>
                                    {polygon.name}
                                  </SelectItem>
                                ))}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="area-select">Hole or Area</Label>
                        <Select
                          name="area-select"
                          value={selectedAreaId ?? undefined}
                          onValueChange={(value) => {
                            setSelectedAreaId(value);
                            setUpdateFormIsDirty(true)
                            // setSelectedCode(codes.find(c => c.id === value) ?? null)
                            // selectedPolyline.codeId = value
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose an area..." />
                          </SelectTrigger>
                          <SelectContent>
                            {areaOptions.length > 0 && (
                              <>
                                {[...areaOptions].sort((a, b) => a.name.localeCompare(b.name)).map((area) => (
                                  <SelectItem key={area.id} value={area.id ?? ""}>
                                    {area.name}
                                  </SelectItem>
                                ))}
                              </>
                            )}
                          </SelectContent>  
                        </Select>
                      </div>

                      <div className="flex justify-evenly pt-2">
                        <FileUploadMain 
                          tenantId={params.tenantId!}
                          siteId={params.siteId!}
                          entityId={selectedGeometryId}
                          entityType="Feature"
                          showMainImage={false}
                          onUploadComplete={(attachment) => {
                            setSelectedMainImage(attachment)
                          }} />
                      </div>
                      
                      {selectedMainImage && 
                      <div className="pt-1">
                        <AttachmentImage
                          url={selectedMainImage.url}
                          isProcessing={selectedMainImage.isProcessing}
                          className="rounded-lg cursor-pointer"
                          title="main feature image"
                          alt="Main feature image"
                          role="button"
                          tabIndex={0}
                          onClick={() => setShowMainImageDialog(true)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") setShowMainImageDialog(true)
                          }}
                        />
                      </div>}
                      <div>
                        <Label htmlFor="item-select">Current Condition</Label>
                        <Select
                          value={selectedCurrentConditionCodeId}
                          onValueChange={(value) => {
                            setSelectedCurrentConditionCodeId(value)
                            setUpdateFormIsDirty(true)
                          }}
                          name="item-select"
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a condition..." />
                          </SelectTrigger>
                          <SelectContent>
                            {conditionCodes.length > 0 && (
                              <>
                                {conditionCodes.map((code) => (
                                  <SelectItem key={code.id} value={code.id}>
                                    {code.name}
                                  </SelectItem>
                                ))}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="polygon-name">Condition Summary</Label>
                        <Textarea
                          id="polygon-name"
                          value={selectedCurrentConditionSummary}
                          onChange={(e) => {
                            setSelectedCurrentConditionSummary(e.target.value)
                            setUpdateFormIsDirty(true)
                          }}
                          placeholder="Enter condition summary..."
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              //savePolygon()
                            }
                          }}
                        />
                      </div>
                      <div>
                        <Label htmlFor="priority-select">Priority</Label>
                        <Select
                          value={selectedPriorityCodeId}
                          onValueChange={(value) => {
                            setSelectedPriorityCodeId(value)
                            setUpdateFormIsDirty(true)
                          }}
                          name="priority-select"
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a priority..." />
                          </SelectTrigger>
                          <SelectContent>
                            {priorityCodes.length > 0 && (
                              <>
                                {priorityCodes.map((code) => (
                                  <SelectItem key={code.id} value={code.id}>
                                    {code.name}
                                  </SelectItem>
                                ))}
                              </>
                            )}                  

                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex justify-evenly gap-1 pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onShowDeleteFeatureDialogClick()}
                          className="w-full bg-red-500"
                        >
                          Delete
                        </Button>            
                      </div>
                    </TabsContent>
                    <TabsContent className="TabsContent" value="tabGallery">
                      <FeatureGallery featureId={selectedGeometryId} siteId={params.siteId!} tenantId={params.tenantId!} />
                    </TabsContent>
                    <TabsContent className="TabsContent" value="tabObservations">
                      <MapObservations featureId={selectedGeometryId} />
                    </TabsContent>                    
                  </Tabs>  
                  </>)}                  
                

                </>
              )}
              {showFeatureDetailsDialog && showNewGeometerySection && (
                <form onSubmit={handleNewFeatureSubmit}>
                  <div>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="new-name">Name*</Label>
                        <Input
                          id="new-name"
                          placeholder="Enter feature name..."
                          {...register("name", { required: 'Name is required'})}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              //savePolygon()
                            }
                          }}
                        />
                        <span className="text-sm text-red-500">{errors.name?.message}</span>
                      </div>
                      <div>
                        <Label htmlFor="areaId">Hole or Area</Label>
                        <select
                          id="areaId"
                          className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
                          {...register("areaId", { setValueAs: (value) => (value === "" ? null : value),})}
                        >
                          <option value="">
                            Select an area
                          </option>
                            {areaOptions.length > 0 && (
                              <>
                                {areaOptions
                                  .map((area) => (
                                    <option key={area.id} value={area.id}>
                                      {area.name}
                                    </option>
                                  ))}
                              </>
                            )}
                        </select> 
                        {/* <Select
                          {...register("areaId" )}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose an area..." />
                          </SelectTrigger>
                          <SelectContent>
                            {areas.current.length > 0 && (
                              <>
                                {areas.current.sort((a, b) => a.name.localeCompare(b.name)).map((area) => (
                                  <SelectItem key={area.id} value={area.id ?? ""}>
                                    {area.name}
                                  </SelectItem>
                                ))}
                              </>
                            )}
                          </SelectContent>
                        </Select> */}
                      </div>
                      <div>
                        <Label htmlFor="new-habitat-select">Feature Type*</Label>
                        <select
                          id="new-habitat-select"
                          className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
                          {...register("codeId", {
                            onChange: (event) => {
                              const featureType = featureTypeOptions.find(ft => ft.id === event.target.value);
                              selectFeatureType(featureType?.featureCodeId ?? null);
                              setHackSelectedType(null);
                            },
                          })}
                        >
                          <option value="">
                            Select a feature type
                          </option>
                          
                            {featureTypeOptions.length > 0 && (
                              <>
                                {featureTypeOptions.map((type) => (
                                    <option key={type.id} value={type.id}>
                                      {type.name}
                                    </option>
                                ))}
                              </>
                            )}
                        </select>
                        <span className="text-sm text-red-500">{errors.codeId?.message}</span>
                      </div>
                      <div className="flex justify-evenly p-2">
                        {selectedHabitatTypes.map((t) => (
                          <Button
                            key={t}
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => onSelectType(t)}
                            className=""
                            disabled={t === hackSelectedType && hackSelectedType != null }
                          >
                            {getGeometeryEmoji(t)} {getGeometeryName(t)}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-2 flex flex-row">
                    <Button
                      size="sm"
                      type="button"
                      variant="outline"
                      disabled={!showNewGeometerySection}
                      onClick={() => onCancelNewGeometery()}
                      className="w-full mr-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      type="submit"
                      variant="outline"
                      disabled={selectedHabitatTypes.length < 1}
                      className="w-full bg-sky-400 ml-1"
                    >
                      Create
                    </Button>
                  </div>
                </form>
              )}
              </CardContent>
            </Card>
        </div>
      </div>

      {/* API Error Alert - positioned at top */}
      {apiError && (
        <div className="absolute top-16 left-4 right-4 z-40 lg:right-auto lg:w-96">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>API Configuration Required:</strong> {apiError}
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Edit Polygon Dialog */}
      <Dialog open={showDeleteFeatureDialog} onOpenChange={setShowDeleteFeatureDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Feature</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p>Do you want to delete the feature <b>{deleteFeatureName}</b>? Deleting a feature cannot be undone.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteFeatureDialog(false)}>
              Cancel
            </Button>
            <Button onClick={onDeleteFeatureClick} className="bg-red-500">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImagePreviewDialog
        open={showMainImageDialog && (!!selectedMainImage?.url || !!selectedMainImage?.isProcessing)}
        onOpenChange={setShowMainImageDialog}
        title="Main feature image"
        imageUrl={selectedMainImage?.url}
        alt="Main feature image"
        dateTaken={selectedMainImage?.dateTaken}
        createdByName={selectedMainImage?.createdByName}
        isProcessing={selectedMainImage?.isProcessing}
      />
    </div>
  )
}
